import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Settings() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<any>(null);

  const { data, isError } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: async () => (await api.get("/admin/settings")).data,
  });

  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = async () => {
    try {
      await api.patch("/admin/settings", {
        ingestion_enabled: form.ingestion_enabled,
        ai_features_enabled: form.ai_features_enabled,
        newsletter_enabled: form.newsletter_enabled,
        paywall_enabled: form.paywall_enabled,
        ai_max_enrich_per_run: Number(form.ai_max_enrich_per_run),
        dedupe_similarity_threshold: Number(form.dedupe_similarity_threshold),
      });
      toast.success("Settings saved");
      queryClient.invalidateQueries({ queryKey: ["admin-settings"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  if (isError) {
    return <div data-testid="admin-settings-page">
      <PageHeader title="System Settings" />
      <p className="text-yellow-400 text-sm">SuperAdmin access required.</p>
    </div>;
  }
  if (!form) return <div className="text-gray-500 py-10">Loading settings...</div>;

  const Row = ({ label, sub, field }: { label: string; sub: string; field: string }) => (
    <div className="flex items-center justify-between bg-[#121212] border border-white/10 rounded-lg p-5">
      <div>
        <p className="text-white font-medium">{label}</p>
        <p className="text-gray-500 text-sm">{sub}</p>
      </div>
      <Switch checked={!!form[field]} data-testid={`setting-${field}`}
        onCheckedChange={(v) => setForm({ ...form, [field]: v })} />
    </div>
  );

  return (
    <div data-testid="admin-settings-page">
      <PageHeader title="System Settings" sub="Global automation and feature controls (SuperAdmin only)" />
      <div className="max-w-2xl space-y-4">
        <Row label="Ingestion engine" sub="Master switch for all scheduled source polling" field="ingestion_enabled" />
        <Row label="AI features" sub="Summaries, key takeaways and enrichment during ingest" field="ai_features_enabled" />
        <Row label="Newsletter system" sub="Digest building and sending" field="newsletter_enabled" />
        <Row label="Premium paywall" sub="Gate premium-flagged content (Stripe deferred)" field="paywall_enabled" />
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
            <Label className="text-white">AI enrichments per run</Label>
            <p className="text-gray-500 text-xs mt-1 mb-2">Caps LLM spend per ingestion run</p>
            <Input type="number" min={0} value={form.ai_max_enrich_per_run}
              data-testid="setting-ai-max-enrich"
              onChange={(e) => setForm({ ...form, ai_max_enrich_per_run: e.target.value })}
              className="bg-[#1A1A1A] border-white/10 text-white" />
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
            <Label className="text-white">Dedupe similarity</Label>
            <p className="text-gray-500 text-xs mt-1 mb-2">0–1, higher = stricter matching</p>
            <Input type="number" step="0.05" min={0} max={1} value={form.dedupe_similarity_threshold}
              data-testid="setting-dedupe-threshold"
              onChange={(e) => setForm({ ...form, dedupe_similarity_threshold: e.target.value })}
              className="bg-[#1A1A1A] border-white/10 text-white" />
          </div>
        </div>
        <Button onClick={save} data-testid="settings-save-button"
          className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">Save settings</Button>
      </div>
    </div>
  );
}
